// Секция «Сетка» — grila de grafice live cu filtre-preset.
//
// Graficele se creează leneș: cardurile din afara ecranului au doar antetul, iar instanța
// Lightweight Charts apare la intrarea în viewport. Fără asta, 40 de grafice ar bloca pagina.

import { el, clear, fmtVolume, fmtCount, fmtPct, debounce, tfLabel, bus } from '../core/util.js';
import { iconBursa, iconMoneda } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { settings, market } from '../core/state.js';
import { Chart } from '../chart/chart.js';
import { TIMEFRAMES, EXCHANGE_LIST } from '../core/exchanges.js';
import { apply, ALL_COINS, BOOKMARKS } from '../core/filters.js';
import { openFilterModal } from '../ui/filter-modal.js';
import { dropdown, marketToggle, tfButtons, toast, modal, baseTicker, tigerBtn } from '../ui/components.js';
import { attachSearchDropdown } from '../ui/search-dropdown.js';
import { openChartModal } from '../ui/chart-modal.js';

const COLUMN_OPTIONS = [2, 3, 4];

export class GridSection {
  constructor(app) {
    this.app = app;
    this.cards = new Map();          // symbol -> { element, chart, observer }
    this.search = '';
    this.unsubs = [];
  }

  get preset() {
    const id = settings.get('sx_screener_active_filter', 'all');
    if (id === 'all') return ALL_COINS;
    if (id === 'bookmarks') return BOOKMARKS;
    const found = (settings.get('sx_screener_filters', []) || []).find((f) => f.id === id);
    return found || ALL_COINS;
  }

  mount(host) {
    this.host = host;
    this.root = el('div', { class: 'grid-section' });
    this.root.append(this.#toolbar(), this.#gridArea());
    host.append(this.root);

    this.unsubs.push(bus.on('market:aggregates', () => this.render()));
    this.unsubs.push(bus.on('market:tick', debounce(() => this.#refreshMetrics(), 500)));
    this.render();
  }

  unmount() {
    this.offDrop?.();
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
    this.#closeFilterMenu();
    for (const card of this.cards.values()) this.#destroyCard(card);
    this.cards.clear();
    this.root?.remove();
  }

  // ------------------------------------------------------------- bara de sus

  #toolbar() {
    const bar = el('div', { class: 'screener-bar' });

    const searchInput = el('input', {
      class: 'search-input',
      placeholder: 'Поиск монеты...',
      oninput: debounce((e) => { this.search = e.target.value.trim().toUpperCase(); this.render(); }, 200),
    });

    // Aceleași sugestii ca în lista de monede: bursă și piață pe fiecare rând.
    this.offDrop?.();
    this.offDrop = attachSearchDropdown(searchInput, (item) => {
      searchInput.value = '';
      this.search = '';
      this.app.openInstrument(item);
    });

    bar.append(
      el('div', { class: 'search-box' }, [icon('search', 14), searchInput]),

      marketToggle(this.app.market, (value) => this.app.setMarket(value)),

      dropdown({
        label: 'Биржа',
        items: EXCHANGE_LIST.map((e) => ({ value: e.key, label: e.label,
          nod: () => iconBursa(e.key, 15) })),
        value: this.app.exchange,
        onSelect: (value) => this.app.setExchange(value),
        className: 'dd-exchange',
      }),

      this.#filterNavigator(),

      el('div', { class: 'bar-sep' }),

      tfButtons(settings.get('sx_screener_grid_tf', '5m'), (tf) => {
        settings.set('sx_screener_grid_tf', tf);
        this.#applyTimeframe(tf);
      }),

      el('div', { class: 'bar-sep' }),

      this.#modeButtons(),
      this.#columnButtons(),
      this.#actionButtons(),
    );
    return bar;
  }

  /** Прокрутка (un singur pânză) vs Страницы (două rânduri pe ecran). */
  #modeButtons() {
    const group = el('div', { class: 'col-group' });
    const active = settings.get('sx_grid_mode', 'scroll');
    for (const [id, iconName, title] of [
      ['scroll', 'scrollMode', 'Прокрутка: все монеты одним полотном'],
      ['pages', 'pageMode', 'Страницы: два ряда графиков на всю высоту экрана'],
    ]) {
      group.append(el('button', {
        class: `col-btn${id === active ? ' active' : ''}`,
        title,
        onclick: (event) => {
          settings.set('sx_grid_mode', id);
          [...group.children].forEach((c) => c.classList.remove('active'));
          event.currentTarget.classList.add('active');
          this.#applyMode();
        },
        dataset: { id },
      }, [icon(iconName, 13)]));
    }
    return group;
  }

  #applyMode() {
    const pages = settings.get('sx_grid_mode', 'scroll') === 'pages';
    this.gridEl.classList.toggle('pages', pages);
    for (const card of this.cards.values()) card.chart?.resize();
  }

  #filterNavigator() {
    const label = el('span', { class: 'sfs-label' });
    const iconSlot = el('span', { class: 'sfs-icon' });
    const update = () => {
      label.textContent = this.preset.name;
      clear(iconSlot).append(icon(this.preset.icon, 13));
    };

    const cycle = (delta) => {
      const list = this.#allPresets();
      const index = list.findIndex((p) => p.id === this.preset.id);
      const next = list[(index + delta + list.length) % list.length];
      this.setPreset(next.id);
    };

    this.updateNavigator = update;
    update();

    const current = el('button', {
      class: 'sfs-current', title: 'Все фильтры',
      onclick: (event) => { event.stopPropagation(); this.#toggleFilterMenu(current); },
    }, [iconSlot, label]);

    this.filterCaret = el('button', {
      class: 'sfs-caret', title: 'Все фильтры',
      onclick: (event) => { event.stopPropagation(); this.#toggleFilterMenu(current); },
    }, [icon('chevronDown', 12)]);

    return el('div', { class: 'sfs' }, [
      el('button', { class: 'sfs-arrow', title: 'Предыдущий фильтр', onclick: () => cycle(-1) }, [icon('chevronLeft', 13)]),
      current,
      el('button', { class: 'sfs-arrow', title: 'Следующий фильтр', onclick: () => cycle(1) }, [icon('chevronRight', 13)]),
      this.filterCaret,
    ]);
  }

  /**
   * Lista de filtre ca un singur popup ancorat de buton — nu ca un panou desfășurat
   * sub bară, cum era. Fiecare rând are editare și ștergere la hover, iar ultimul
   * rând creează un filtru nou.
   */
  #toggleFilterMenu(anchor) {
    if (this.filterMenu) { this.#closeFilterMenu(); return; }

    const menu = el('div', { class: 'sfs-menu' });
    for (const preset of this.#allPresets()) {
      const editable = !['all', 'bookmarks'].includes(preset.id);
      menu.append(el('div', {
        class: `sfs-item${preset.id === this.preset.id ? ' active' : ''}`,
      }, [
        el('button', {
          class: 'sfs-item-main',
          title: editable ? describe(preset) : preset.name,
          onclick: () => { this.setPreset(preset.id); this.#closeFilterMenu(); },
        }, [icon(preset.icon, 14), el('span', { text: preset.name })]),
        editable ? el('button', {
          class: 'sfs-item-btn', title: 'Редактировать',
          onclick: () => { this.#closeFilterMenu(); this.#editPreset(preset); },
        }, [icon('pencil', 11)]) : null,
        editable ? el('button', {
          class: 'sfs-item-btn danger', title: 'Удалить',
          onclick: () => { this.#closeFilterMenu(); this.#deletePreset(preset); },
        }, [icon('trash', 11)]) : null,
      ]));
    }

    menu.append(el('button', {
      class: 'sfs-create',
      onclick: () => { this.#closeFilterMenu(); this.#editPreset(null); },
    }, [icon('plus', 13), el('span', { text: 'Создать фильтр' })]));

    document.body.append(menu);
    const rect = anchor.getBoundingClientRect();
    menu.style.left = `${rect.left}px`;
    menu.style.top = `${rect.bottom + 6}px`;
    const box = menu.getBoundingClientRect();
    if (box.right > window.innerWidth - 8) {
      menu.style.left = `${Math.max(8, window.innerWidth - box.width - 8)}px`;
    }

    this.filterMenu = menu;
    this.filterCaret?.classList.add('open');
    this.closeFilterMenuOutside = (event) => {
      if (!menu.contains(event.target)) this.#closeFilterMenu();
    };
    setTimeout(() => document.addEventListener('mousedown', this.closeFilterMenuOutside), 0);
  }

  #closeFilterMenu() {
    if (!this.filterMenu) return;
    this.filterMenu.remove();
    this.filterMenu = null;
    this.filterCaret?.classList.remove('open');
    document.removeEventListener('mousedown', this.closeFilterMenuOutside);
  }

  #columnButtons() {
    const group = el('div', { class: 'col-group' });
    const active = settings.get('sx_screener_cols', 3);
    for (const n of COLUMN_OPTIONS) {
      group.append(el('button', {
        class: `col-btn${n === active ? ' active' : ''}`,
        text: String(n),
        title: `${n} колонки`,
        onclick: () => {
          settings.set('sx_screener_cols', n);
          [...group.children].forEach((c) => c.classList.remove('active'));
          group.querySelector(`[data-n="${n}"]`)?.classList.add('active');
          this.#applyColumns();
        },
        dataset: { n },
      }));
    }
    return group;
  }

  #actionButtons() {
    const wrap = el('div', { class: 'bar-actions' });

    /** Comutator persistat, cu efect imediat asupra tuturor cardurilor. */
    const toggleBtn = (key, iconName, title, apply) => {
      const on = settings.get(key, false);
      return el('button', {
        class: `bar-icon-btn${on ? ' active' : ''}`,
        title,
        onclick: (event) => {
          const next = !settings.get(key, false);
          settings.set(key, next);
          event.currentTarget.classList.toggle('active', next);
          apply(next);
        },
      }, [icon(iconName, 14)]);
    };

    wrap.append(
      toggleBtn('sx_levels_hidden', 'levels',
        'Скрыть уровни поддержки/сопротивления на графиках',
        (hidden) => { for (const c of this.cards.values()) c.chart?.setLevelsVisible(!hidden); }),

      toggleBtn('sx_grid_hidden', 'eyeOff', 'Скрыть сетку на графиках',
        (hidden) => { for (const c of this.cards.values()) c.chart?.setGridVisible(!hidden); }),

      toggleBtn('sx_axes_hidden', 'textSlash', 'Скрыть значения цен/времени на графике',
        (hidden) => { for (const c of this.cards.values()) c.chart?.setAxesVisible(!hidden); }),

      el('button', {
        class: 'bar-icon-btn', title: 'Обновить данные',
        onclick: () => { market.refreshAggregates(); toast('Данные обновлены'); },
      }, [icon('refresh', 14)]),

      el('button', {
        class: 'bar-icon-btn', title: 'Памятка: что такое фильтры и как ими пользоваться',
        onclick: () => this.#openHelp(),
      }, [icon('help', 14)]),
    );
    return wrap;
  }

  #openHelp() {
    const items = [
      ['Фильтр', 'Набор условий (объём, изменение, волатильность…). Монета попадает в сетку, '
        + 'только если проходит все включённые условия.'],
      ['Диапазон', 'У каждого условия есть «от» и «до». Оставьте поле пустым — граница не применяется.'],
      ['Сортировка', 'Определяет, какие монеты окажутся первыми, когда фильтр проходит больше монет, '
        + 'чем помещается в сетке.'],
      ['Максимум монет', 'Сколько графиков рисовать. Остальные отсекаются по сортировке.'],
      ['Чёрный список', 'Монеты, которые никогда не показывать — даже если проходят фильтр.'],
    ];
    modal({
      title: 'Как работают фильтры',
      width: 560,
      body: el('div', { class: 'dash-about' }, items.map(([k, v]) => el('div', { class: 'dash-about-row' }, [
        el('span', { class: 'dash-about-key', text: k }),
        el('span', { class: 'dash-about-val', text: v }),
      ]))),
    });
  }

  // ------------------------------------------------------------------ filtre

  #allPresets() {
    return [ALL_COINS, BOOKMARKS, ...(settings.get('sx_screener_filters', []) || [])];
  }

  #editPreset(preset) {
    openFilterModal(preset, (saved) => {
      const list = [...(settings.get('sx_screener_filters', []) || [])];
      const index = list.findIndex((f) => f.id === saved.id);
      if (index >= 0) list[index] = saved; else list.push(saved);
      settings.set('sx_screener_filters', list);
      this.setPreset(saved.id);
      toast(index >= 0 ? 'Фильтр обновлён' : 'Фильтр создан', 'success');
    });
  }

  #deletePreset(preset) {
    modal({
      title: 'Удалить фильтр?',
      body: el('div', { class: 'confirm-text', text: `Фильтр «${preset.name}» будет удалён безвозвратно.` }),
      width: 420,
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Удалить', primary: true,
          onClick: (c) => {
            const list = (settings.get('sx_screener_filters', []) || []).filter((f) => f.id !== preset.id);
            settings.set('sx_screener_filters', list);
            if (this.preset.id === preset.id) this.setPreset('all');
            c();
          },
        },
      ],
    });
  }

  setPreset(id) {
    settings.set('sx_screener_active_filter', id);
    this.updateNavigator?.();
    this.render();
  }

  // -------------------------------------------------------------------- grilă

  #gridArea() {
    this.gridEl = el('div', { class: 'chart-grid' });
    this.#applyColumns();
    this.emptyEl = el('div', { class: 'grid-empty', text: 'Ни одна монета не проходит фильтр' });
    // `.grid-area` este elementul care derulează — el trebuie să fie rădăcina
    // IntersectionObserver-ului, altfel toate cardurile par vizibile deodată.
    this.scrollEl = el('div', { class: 'grid-area' }, [this.gridEl, this.emptyEl]);
    return this.scrollEl;
  }

  #applyColumns() {
    const cols = settings.get('sx_screener_cols', 3);
    this.gridEl.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
    // La patru coloane antetul nu mai încape: măsurat, conţinutul cere 329 px într-un
    // spaţiu de 303. Marcăm lăţimea, iar CSS-ul scoate butonul care dublează un altul.
    this.gridEl.classList.toggle('cols-4', cols >= 4);
    this.#applyMode();
  }

  #applyTimeframe(tf) {
    for (const [symbol, card] of this.cards) {
      const perCard = (settings.get('sx_cell_tfs', {}) || {})[symbol];
      if (perCard) continue;              // override per card — nu-l călcăm
      card.chart?.setTimeframe(tf);
      card.tfGroup?.querySelectorAll('.tf-btn').forEach((b) => {
        b.classList.toggle('active', b.dataset.tf === tf);
      });
    }
  }

  /** Simbolurile care trebuie afișate acum. */
  symbols() {
    const preset = this.preset;
    let rows = market.list();

    if (preset.id === 'bookmarks') {
      const marks = new Set(settings.get('sx_bookmarks', []) || []);
      rows = rows.filter((r) => marks.has(r.symbol));
      return rows.sort((a, b) => b.quoteVolume - a.quoteVolume).map((r) => r.symbol);
    }
    if (this.search) rows = rows.filter((r) => r.symbol.includes(this.search));
    return apply(preset, rows).visible.map((r) => r.symbol);
  }

  render() {
    if (!this.gridEl) return;
    const wanted = this.symbols();
    const wantedSet = new Set(wanted);

    for (const [symbol, card] of this.cards) {
      if (!wantedSet.has(symbol)) {
        this.#destroyCard(card);
        this.cards.delete(symbol);
      }
    }

    for (const symbol of wanted) {
      if (!this.cards.has(symbol)) this.cards.set(symbol, this.#createCard(symbol));
    }
    // Reordonăm nodurile ca să respecte ordinea sortării.
    for (const symbol of wanted) this.gridEl.append(this.cards.get(symbol).element);

    this.emptyEl.style.display = wanted.length ? 'none' : 'block';
    this.#refreshMetrics();
  }

  #createCard(symbol) {
    const row = market.get(symbol) || {};
    const tf = (settings.get('sx_cell_tfs', {}) || {})[symbol]
      || this.preset.chartTf
      || settings.get('sx_screener_grid_tf', '5m');

    const metricsEl = el('div', { class: 'card-metrics' });
    const chartEl = el('div', { class: 'card-chart' });

    const tfGroup = tfButtons(tf, (value) => {
      const map = { ...(settings.get('sx_cell_tfs', {}) || {}) };
      map[symbol] = value;
      settings.set('sx_cell_tfs', map);
      card.chart?.setTimeframe(value);
    }, { compact: true });

    const bookmarks = new Set(settings.get('sx_bookmarks', []) || []);
    const bookmarkBtn = el('button', {
      class: `card-btn${bookmarks.has(symbol) ? ' on' : ''}`,
      title: 'В закладки',
      onclick: (event) => {
        const marks = new Set(settings.get('sx_bookmarks', []) || []);
        if (marks.has(symbol)) marks.delete(symbol); else marks.add(symbol);
        settings.set('sx_bookmarks', [...marks]);
        event.currentTarget.classList.toggle('on', marks.has(symbol));
      },
    }, [icon('bookmark', 13)]);

    const element = el('div', { class: 'grid-card' }, [
      el('div', { class: 'card-head' }, [
        // Clic pe nume = tickerul în clipboard. Aceeaşi purtare ca pe antetul graficului
        // mare: dacă într-un loc numele se copiază, în celălalt trebuie să facă la fel,
        // altfel utilizatorul trebuie să ţină minte unde merge şi unde nu.
        iconMoneda(symbol, 16),
        el('span', {
          class: 'card-ticker', text: baseTicker(symbol), title: 'Скопировать тикер',
          onclick: () => {
            navigator.clipboard?.writeText(symbol);
            toast('Тикер скопирован в буфер обмена');
          },
        }),
        el('span', { class: 'card-mkt', text: this.app.market === 'futures' ? '.F' : '.S' }),
        tigerBtn(symbol, this.app.exchange, this.app.market),
        bookmarkBtn,
        el('div', { class: 'card-head-spacer' }),
        tfGroup,
        // Doar fereastra separată: ecranul complet al browserului a fost scos la cererea
        // utilizatorului — două butoane de „mărire" alăturate se confundau.
        el('button', {
          class: 'card-btn', dataset: { act: 'expand' }, title: 'Открыть в отдельном окне',
          onclick: () => openChartModal({
            symbol, exchange: this.app.exchange, market: this.app.market, tf: card.chart?.tf || tf,
          }),
        }, [icon('expand', 12)]),
      ]),
      metricsEl,
      chartEl,
    ]);

    const card = { symbol, element, metricsEl, chartEl, tfGroup, chart: null, tf };

    // Instanțiem graficul doar când cardul intră în ecran.
    card.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !card.chart) {
          card.chart = new Chart(chartEl, {
            exchange: this.app.exchange,
            market: this.app.market,
            symbol,
            tf,
            compact: true,
            limit: 300,
            indicators: { volume: true },
            showLevels: !settings.get('sx_levels_hidden', false),
          });
          if (settings.get('sx_grid_hidden', false)) card.chart.setGridVisible(false);
          if (settings.get('sx_axes_hidden', false)) card.chart.setAxesVisible(false);
          card.chart.load().catch((err) => {
            chartEl.append(el('div', { class: 'card-error', text: 'Не удалось загрузить свечи' }));
            console.warn(`[grid] ${symbol}: ${err.message}`);
          });
          card.observer.disconnect();
        }
      }
    }, { root: this.scrollEl, rootMargin: '300px' });
    card.observer.observe(element);

    this.#renderMetrics(card, row);
    return card;
  }

  /** Rândul de cifre de sub antet: оборот↓ · изменение · сделки · всплеск · волатильность. */
  #renderMetrics(card, row) {
    clear(card.metricsEl);
    if (!row) return;
    const change = Number(row.change24h) || 0;
    const spike = Number(row.volTrend) || 0;
    const cell = (text, cls = '', title = '') => el('span', { class: `cm ${cls}`, text, title });

    const volume = el('span', { class: 'cm cm-vol', title: 'Оборот 24ч' }, [
      el('span', { text: fmtVolume(row.quoteVolume) }),
    ]);
    if (Number.isFinite(row.volTrend)) {
      const arrow = icon(spike >= 0 ? 'arrowUp' : 'arrowDown', 8);
      arrow.classList.add(spike >= 0 ? 'up' : 'down');
      volume.append(arrow);
    }

    card.metricsEl.append(
      volume,
      cell(fmtPct(change), change >= 0 ? 'up' : 'down', 'Изменение 24ч'),
      cell(fmtCount(row.trades), '', 'Сделки 24ч'),
      cell(fmtPct(spike, 0), spike >= 0 ? 'up' : 'down', 'Всплеск объёма'),
      cell(`${(row.volatility24h ?? 0).toFixed(2)}%`, '', 'Волатильность'),
    );
  }

  #refreshMetrics() {
    for (const [symbol, card] of this.cards) {
      this.#renderMetrics(card, market.get(symbol));
    }
  }

  #destroyCard(card) {
    card.observer?.disconnect();
    card.chart?.destroy();
    card.element.remove();
  }
}

function describe(preset) {
  const parts = [];
  if (preset.volumeEnabled) parts.push(`объём от ${fmtVolume(preset.volumeMin || 0)}`);
  if (preset.changeEnabled) parts.push(`изм. ${preset.changeMin ?? '−∞'}…${preset.changeMax ?? '∞'}%`);
  if (preset.volatilityEnabled) parts.push(`волат. ${preset.volatilityMin ?? 0}…${preset.volatilityMax ?? '∞'}%`);
  if (preset.tradesEnabled) parts.push(`сделок от ${preset.tradesMin ?? 0}`);
  return parts.length ? parts.join(' · ') : 'Без условий';
}
